import {useState, Fragment} from 'react'
import {List, Map} from 'immutable'
import PropTypes from 'prop-types'
import {useMutation} from '@apollo/client'
import classnames from 'classnames'

import PrintIcon from '@mui/icons-material/LocalPrintshopOutlined'
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined'

import BackupCodeIcon from '../../BackupCodeIcon'
import {Button, Cancel, Remove} from '../../../../../dashboard/src/components/blocks/Buttons'
import ButtonGroup from '../../../../../shared_components/core/buttons/ButtonGroup'
import {BoxGutterMedium} from '../../../../../dashboard/src/components/blocks/Boxes'
import {ContentBoxGutterMedium, ContentBox} from '../../../../../dashboard/src/components/blocks/ContentBoxes'
import ManageTfaSetupFrame from '../ManageTfaSetupFrame'
import SmallModal from '../../../../../shared_components/mui_base_components/modals/small_modal/SmallModal'
import {TextGutterMedium} from '../../../../../dashboard/src/components/blocks/Texts'
import UnorderedList from '../../../../../shared_components/UnorderedList'
import SuccessMessage from './SuccessMessage'

import {BACKUP_CODES} from '../../../../../graphql/queries/tfa'
import {CREATE_TFA_BACKUP_CODES} from '../../../../../graphql/mutations/tfa'
import {cleanGraphQLResponse} from '../../../../../lib/graphQLTools'
import {pluralize} from '../../../../../lib/tools'
import copyIcon from '../../../../../shared_assets/copyIcon.png'

const RecoveryCodes = props => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const {status: backupCodesStatus, mode} = props.backupCodesStatus
  const [status, setStatus] = useState(backupCodesStatus || 'initial')
  const [modalMode, setModalMode] = useState('delete')
  const [feedback, setFeedback] = useState('Copy All')
  const [createTfaBackupCodes, {data}] = useMutation(CREATE_TFA_BACKUP_CODES, {awaitRefetchQueries: true, refetchQueries: [{query: BACKUP_CODES}]})
  const tfaBackup = cleanGraphQLResponse(data, 'createTfaBackupCodes', props.tfaBackup)

  const toggleModal = () => setShowModal(!showModal)

  const generateBackupCodes = () => {
    setLoading(true)

    createTfaBackupCodes({
      variables: {tfaConfigId: props.tfaConfig.get('id')},
      onCompleted: () => {
        setLoading(false)
        setStatus('generated')
        toggleModal()
      }
    })
  }

  const handleCopyCodes = async () => {
    if (!(navigator.clipboard && navigator.clipboard.writeText))
      return

    await navigator.clipboard.writeText(tfaBackup.get('backup-codes', List()).join('\n'))
    setFeedback('Copied')
  }

  const handleDownload = () => {
    if (!(window.Blob && window.URL && window.URL.createObjectURL))
      return

    const file = new Blob([tfaBackup.get('backup-codes', List()).join('\n')], {type: 'text/plain;charset=utf-8'})
    const downloadUrl = window.URL.createObjectURL(file)
    const anchor = document.createElement('a')

    anchor.href = downloadUrl
    anchor.download = 'everplans-backup-codes.txt'
    anchor.click()
    window.URL.revokeObjectURL(downloadUrl)
  }

  const onConfirmation = () => {
    if (mode && mode !== 'recovery') {
      setModalMode('success')
      toggleModal()
    } else {
      props.toggleModal()
    }
  }

  return (
    <Fragment>
      <ManageTfaSetupFrame
        headerIcon={<BackupCodeIcon />}
        headerTitle='Manage backup codes'
        headerSubtitle='Configure your backup code preferences.'
        removeText='Generate new codes'
        warningText='This will invalidate all existing backup codes, including unused ones.'
        primaryAction={toggleModal}>
        <ContentBoxGutterMedium className='body'>
          <h3>You have {props.backupCodes.size} remaining {pluralize('code', props.backupCodes.size)}.</h3>
          <TextGutterMedium>Each code can only be used once. Store them securely.</TextGutterMedium>
          {
            status === 'generated' && (
              <ContentBoxGutterMedium className='new-codes'>
                {
                  tfaBackup.get('backup-codes', List()).map(code => <TextGutterMedium key={code} className='code'>{code.match(/(.{1,4})/g).join('\u2015')}</TextGutterMedium>)
                }
                <ContentBox>
                  <ButtonGroup>
                    <Button onClick={handleCopyCodes}><img src={copyIcon} alt='Copy' /> {feedback}</Button>
                    <Button onClick={() => window.print()}><PrintIcon /> Print</Button>
                    <Button onClick={handleDownload}><FileDownloadIcon /> Download</Button>
                  </ButtonGroup>
                </ContentBox>
              </ContentBoxGutterMedium>
            )
          }
          <BoxGutterMedium className='recovery-codes-info-box'>
            <h4>{status === 'generated' ? 'Where should I store these codes?' : 'Where are my backup codes? We suggested:'}</h4>
            <UnorderedList
              className={classnames({generated: status === 'generated'})}
              children={
                status === 'generated' ?
                  [
                    'Password manager',
                    'Printed and stored in a safe place',
                    'Encrypted cloud storage',
                    'Unencrypted notes or documents'
                  ] :
                  [
                    'Password manager (1Password, LastPass, Bitwarden)',
                    'Printed and stored in a safe place',
                    'Encrypted cloud storage'
                  ]
              }
            />
          </BoxGutterMedium>
          {status === 'generated' && (
            <ButtonGroup>
              <Button className='securely-stored-codes' onClick={onConfirmation}>I have securely stored backup codes</Button>
            </ButtonGroup>
          )}
        </ContentBoxGutterMedium>
      </ManageTfaSetupFrame>
      <SmallModal className='warning-modal' open={showModal} onClose={props.toggleModal}>
        <ContentBox className='content flex-container'>
          {
            modalMode === 'delete' ?
              (
                <Fragment>
                  <h2>Generate new codes</h2>
                  <TextGutterMedium>
                    Generating a new set of codes will permanently expire your current ones. Your previous codes will no longer work. Click continue to generate a new set of codes.
                  </TextGutterMedium>
                  <ButtonGroup>
                    <Cancel onClick={toggleModal} />
                    <Remove onClick={generateBackupCodes} processing={loading}>Continue</Remove>
                  </ButtonGroup>
                </Fragment>
              ) :
              (
                <SuccessMessage mode={mode} onFinish={props.toggleModal} />
              )
          }
        </ContentBox>
      </SmallModal>
    </Fragment>
  )
}

RecoveryCodes.propTypes = {
  backupCodes: PropTypes.instanceOf(List).isRequired,
  backupCodesStatus: PropTypes.shape({status: PropTypes.string, mode: PropTypes.string}),
  tfaConfig: PropTypes.instanceOf(Map).isRequired,
  tfaBackup: PropTypes.instanceOf(Map),
  toggleModal: PropTypes.func.isRequired
}

export default RecoveryCodes
